import { useContext, useState } from 'react';
import { useParams } from 'react-router-dom';
import candidatePagesContext from '@/contexts/candidatePagesContext';
import { useToastContext } from '@/contexts/toastContext';
import useApiFetch from '@/hooks/useApiFetch';
import useApiSubmit from '@/hooks/useApiSubmit';
import jobsService, { AddCandidatesToJobParams } from '@/services/jobs';
import { FetchApiIds } from '@/types';
import { Job } from '@/types/api';
import { onScrollToSmoothly } from '@/utils/global';
import CandidateJobsComponent, { Filter } from './CandidateJobs.component';

const CandidateJobsPage = function (): JSX.Element {
  const [filter, setFilter] = useState<Filter>('All');
  const [jobSearch, setJobSearch] = useState<string>('');
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const { candidate } = useContext(candidatePagesContext);
  const { id = '' } = useParams();
  const toast = useToastContext();
  const [disassociatingFromJobMap, setDisassociatingFromJobMap] = useState<Record<string, boolean>>(
    {}
  );

  const { data: jobsSearchResult, isLoading: isSearchLoading } = useApiFetch(
    [FetchApiIds.JOB_SEARCH, jobSearch],
    async () => jobsService.searchJob(jobSearch)
  );

  const { data: jobs, refetch: refetchJobs } = useApiFetch(
    [FetchApiIds.GET_JOBS_BY_CANDIDATE_ID, id, filter],
    async () =>
      jobsService.getJobsByCandidateId({
        candidateId: id,
        ...(filter !== 'All' && { status: filter === 'Closed' ? 'CLOSED' : 'OPEN' }),
      })
  );

  const addCandidatesToJobMutation = useApiSubmit<unknown, AddCandidatesToJobParams>(
    jobsService.addCandidatesToJob
  );

  const removeCandidatesToJobMutation = useApiSubmit<unknown, AddCandidatesToJobParams>(
    jobsService.removeCandidatesToJob
  );

  const handleRemoveFromJob = async (jobId: string): Promise<void> => {
    try {
      setDisassociatingFromJobMap((map) => ({
        ...map,
        [jobId]: true,
      }));

      await removeCandidatesToJobMutation.mutateAsync({
        jobId,
        candidateIds: [id],
      });

      toast.showSuccessFeedback('Candidate removed successfully');

      setDisassociatingFromJobMap((map) => ({
        ...map,
        [jobId]: false,
      }));

      refetchJobs();
    } catch {
      toast.showErrorFeedback('There was a problem disassociating the candidate from this job');
    }
  };

  const handleAssociateToJob = async (job: Job): Promise<void> => {
    try {
      await addCandidatesToJobMutation.mutateAsync({
        jobId: job.id,
        candidateIds: [id],
      });
      toast.showSuccessFeedback('Job associated successfully');
      refetchJobs();
    } catch {
      toast.showErrorFeedback('There was a problem to associate the candidate to this job');
    }
  };

  return (
    <CandidateJobsComponent
      onScrollTo={onScrollToSmoothly}
      jobs={jobs}
      filter={filter}
      onChangeFilter={setFilter}
      jobsSearchInput={jobSearch}
      onJobsSearchChange={setJobSearch}
      showSearch={showSearch}
      onShowSearchChange={setShowSearch}
      jobSearchItems={jobsSearchResult}
      isSearchLoading={isSearchLoading}
      candidate={candidate}
      onAssociateJob={handleAssociateToJob}
      onDisassociateJob={handleRemoveFromJob}
      disassociatingFromJobMap={disassociatingFromJobMap}
    />
  );
};

export default CandidateJobsPage;
